import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API, APP_NAME } from "@/App";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, User, FileText, Clock, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import * as chrono from "chrono-node";

const IntakesList = () => {
  const navigate = useNavigate();
  const { token, shop } = useContext(AuthContext);
  const [intakes, setIntakes] = useState([]);
  const [filteredIntakes, setFilteredIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("upcoming");
  const [statusFilter, setStatusFilter] = useState("all");
  // const [appointmentTypeFilter, setAppointmentTypeFilter] = useState("all");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");

  useEffect(() => {
    fetchIntakes();
  }, []);

  // Update page title
  useEffect(() => {
    document.title = `Intakes - ${APP_NAME}`;
  }, []);

  useEffect(() => {
    applyFilters();
  }, [intakes, searchQuery, dateRangeFilter, statusFilter, serviceTypeFilter]);

  const fetchIntakes = async () => {
    try {
      const response = await axios.get(`${API}/shop/intakes?type=appointment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntakes(response.data);
    } catch (error) {
      toast.error("Failed to load intakes");
    } finally {
      setLoading(false);
    }
  };

  const extractAppointmentDateTime = (intake) => {
    if (!intake) return null;

    if (intake.appointment_date) {
      return new Date(intake.appointment_date);
    }

    const match = intake.ai_summary.match(/APPOINTMENT DATE\/TIME:\s*(.+)/i);
    if (match) {
      const dateStr = match[1].trim();

      const isoDate = parseToISO(dateStr);

      // Try to parse the date
      const parsed = new Date(isoDate);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return null;
  };

  const parseToISO = (input) => {
    if (!input || typeof input !== "string") return null;

    // First try natural language parsing
    const parsed = chrono.parseDate(input, new Date(), { forwardDate: true });

    if (parsed) {
      return parsed.toISOString();
    }

    // Fallback for common explicit formats
    const cleanedInput = input.trim();

    // Example supported formats:
    // 10/12/2025 05:00 PM
    // 2025-12-10 17:00
    const manualDate = new Date(cleanedInput);

    if (!isNaN(manualDate)) {
      return manualDate.toISOString();
    }

    return null; // Could not parse
  }

  const extractEventType = (summary) => {
    if (!summary) return null;
    const match = summary.match(/EVENT TYPE:\s*(.+)/i);
    return match ? match[1].trim() : null;
  };

  const getIntakeStatus = (intake) => {
    if (intake.status === "completed") {
      const apptDateTime = extractAppointmentDateTime(intake);
      if (apptDateTime) {
        return apptDateTime > new Date() ? "ready_for_appointment" : "completed_passed";
      }
      return "completed_passed";
    }
    return "in_progress";
  };

  const applyFilters = () => {
    let filtered = [...intakes];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((intake) => {
        const clientName = intake.customer_name || "";
        const eventType = extractEventType(intake.ai_summary) || intake.service_type?.name || "";
        return (
          clientName.toLowerCase().includes(query) ||
          eventType.toLowerCase().includes(query)
        );
      });
    }

    // Date range filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const next7Days = new Date(today);
    next7Days.setDate(next7Days.getDate() + 7);
    const next30Days = new Date(today);
    next30Days.setDate(next30Days.getDate() + 30);

    if (dateRangeFilter !== "all") {
      filtered = filtered.filter((intake) => {
        const apptDateTime = extractAppointmentDateTime(intake);
        if (!apptDateTime && dateRangeFilter !== "no_date") return false;
        if (!apptDateTime && dateRangeFilter === "no_date") return true;

        switch (dateRangeFilter) {
          case "today":
            return apptDateTime >= today && apptDateTime < tomorrow;
          case "next_7_days":
            return apptDateTime >= today && apptDateTime < next7Days;
          case "next_30_days":
            return apptDateTime >= today && apptDateTime < next30Days;
          case "upcoming":
            return apptDateTime >= today;
          default:
            return true;
        }
      });
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((intake) => {
        const status = getIntakeStatus(intake);
        return status === statusFilter;
      });
    }

    // // Appointment type filter
    // if (appointmentTypeFilter !== "all") {
    //   filtered = filtered.filter((intake) => {
    //     const eventType = extractEventType(intake.ai_summary) || intake.appointment_type;
    //     return eventType === appointmentTypeFilter;
    //   });
    // }

    // Service type filter
    if (serviceTypeFilter !== "all") {
      filtered = filtered.filter((intake) => {
        return intake.service_type?._id === serviceTypeFilter;
      });
    }

    // Sort: Primary by appointment date/time, Secondary by client name
    filtered.sort((a, b) => {
      const apptA = extractAppointmentDateTime(a);
      const apptB = extractAppointmentDateTime(b);
      const nameA = a.customer_name || "";
      const nameB = b.customer_name || "";

      // If both have appointment dates, sort by date
      if (apptA && apptB) {
        const dateDiff = apptA.getTime() - apptB.getTime();
        if (dateDiff !== 0) return dateDiff;
        return nameA.localeCompare(nameB);
      }

      // Items without appointment date go to bottom
      if (!apptA && apptB) return 1;
      if (apptA && !apptB) return -1;

      // Both without dates, sort by name
      return nameA.localeCompare(nameB);
    });

    setFilteredIntakes(filtered);
  };

  const viewServiceSheet = (intakeId) => {
    navigate(`/dashboard/service-sheet/${intakeId}`);
  };

  const formatAppointmentDateTime = (dateTime) => {
    if (!dateTime) return null;

    const date = new Date(dateTime);

    // Guard against invalid date
    if (isNaN(date.getTime())) return null;

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCreatedDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      in_progress: { label: "In Progress", color: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30" },
      ready_for_appointment: { label: "Ready for Appointment", color: "bg-[#00ced1]/20 text-[#00ced1] border-[#00ced1]/30" },
      completed_passed: { label: "Completed/Passed", color: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
    };
    return badges[status] || badges.in_progress;
  };

  // Separate intakes with and without appointment dates
  const withAppointments = filteredIntakes.filter(i => extractAppointmentDateTime(i));

  if (loading) {
    return <div className="text-[#f8f8f8]/70">Loading intakes...</div>;
  }

  return (
    <div className="max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#f8f8f8] mb-2">Intakes</h1>
        <p className="text-lg text-[#f8f8f8]/70">
          Customer intake summaries ready for your appointments
        </p>
      </div>

      {/* Filters & Search */}
      <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/20 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-[#d4af37]" />
          <h2 className="text-lg font-semibold text-[#f8f8f8]">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#f8f8f8]/40" />
              <Input
                data-testid="search-intakes-input"
                placeholder="Search by client name or event type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40"
              />
            </div>
          </div>

          {/* Date Range */}
          <div>
            <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
              <SelectTrigger className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="next_7_days">Next 7 Days</SelectItem>
                <SelectItem value="next_30_days">Next 30 Days</SelectItem>
                <SelectItem value="no_date">No Appointment Date</SelectItem>
                <SelectItem value="all">All Dates</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="ready_for_appointment">Ready for Appointment</SelectItem>
                <SelectItem value="completed_passed">Completed/Passed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Appointment Type Filter */}
        {shop?.service_types && shop.service_types.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
                <SelectTrigger className="bg-[#0a1a1f]/60 border-[#005f6b] text-[#f8f8f8]">
                  <SelectValue placeholder="Filter by service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Service Types</SelectItem>
                  {shop.service_types.map((type) => (
                    <SelectItem key={type._id} value={type._id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {intakes.length === 0 ? (
        <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/20 p-12 text-center">
          <FileText className="w-12 h-12 text-[#d4af37]/50 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#f8f8f8] mb-2">No Intakes Yet</h3>
          <p className="text-[#f8f8f8]/70">
            Share your intake link with customers to get started!
          </p>
        </div>
      ) : (
        <>
          {/* With Appointments */}
          {withAppointments.length > 0 && (
            <div className="mb-8">
              <div className="grid gap-4">
                {withAppointments.map((intake) => {
                  // const apptDateTime = extractAppointmentDateTime(intake);
                  const status = getIntakeStatus(intake);
                  const badge = getStatusBadge(status);

                  return (
                    <Card
                      key={intake.id}
                      data-testid={`intake-card-${intake.id}`}
                      className="p-6 bg-[#005f6b]/20 backdrop-blur-sm hover:bg-[#005f6b]/30 transition-all cursor-pointer border border-[#d4af37]/20"
                      onClick={() => viewServiceSheet(intake.id)}
                    >
                      <div className="flex justify-between items-start gap-6">
                        {/* Client Name - Big and Bold */}
                        <h3 className="text-2xl font-bold text-[#f8f8f8] mb-2">
                          {intake.customer_name}
                        </h3>

                        {/* CTA */}
                        <Button
                          data-testid={`view-pull-sheet-btn-${intake.id}`}
                          variant="outline"
                          className="border-[#00ced1] text-[#00ced1] hover:bg-[#00ced1]/10 whitespace-nowrap"
                        >
                          View Service Sheet
                        </Button>
                      </div>
                      <div className="flex justify-between items-start gap-6">
                        <div className="flex-1">
                          {/* Event Type */}
                          <p className="text-base text-[#d4af37] font-medium mb-3">
                            {intake.event_type || "Not provided"}
                          </p>

                          {/* Appointment Date/Time - Crucial */}
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-5 h-5 text-[#00ced1]" />
                            <span className="text-lg font-semibold text-[#00ced1]">
                              Appointment: {formatAppointmentDateTime(intake.appointment_date)}
                            </span>
                          </div>

                          {/* Status Badge */}
                          <div className="mb-3">
                            <span className={`text-xs px-3 py-1 rounded-full border ${badge.color}`}>
                              {badge.label}
                            </span>
                          </div>

                          {/* Intake Started */}
                          <div className="flex items-center gap-2 text-sm text-[#f8f8f8]/60">
                            <Calendar className="w-4 h-4" />
                            <span>Intake started: {formatCreatedDate(intake.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {filteredIntakes.length === 0 && intakes.length > 0 && (
            <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/20 p-12 text-center">
              <Search className="w-12 h-12 text-[#d4af37]/50 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#f8f8f8] mb-2">No Results Found</h3>
              <p className="text-[#f8f8f8]/70">
                Try adjusting your filters or search query
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IntakesList;
